import {
  type Customer,
  type OpportunityCustomer,
  fetcher,
  mapCustomerDataToCustomer,
  resetCustomerProfile,
  useCustomerProfile,
  useOpportunity,
} from "@repo/api";
import { logError } from "@repo/utils";
import useSWR from "swr";

import { useSettings } from "../settings/SettingsProvider";
import { type CustomerSource } from "../utils/customerHelpers";

const fetchCustomerFromOpportunity = async (projectId?: number) => {
  if (!projectId) {
    return undefined;
  }
  try {
    const customerResponse = await fetcher.opportunity.get<OpportunityCustomer>(
      `/${projectId}/customer`
    );
    return customerResponse?.customer;
  } catch (e) {
    logError(e, [
      {
        tagName: "message",
        tagValue: "Failed to fetch customer from opportunity API",
      },
    ]);
    return undefined;
  }
};

export function useSearchOpportunityCustomer(projectId?: number) {
  const { isLoaded, customerProfile } = useCustomerProfile();
  const { data: opportunity } = useOpportunity(projectId);
  const { market, language } = useSettings();

  const shouldRunCustomerProfileSearch =
    isLoaded === true && customerProfile?.searchCustomer;
  const isReady = isLoaded !== undefined && opportunity?.id !== undefined;

  return useSWR<
    | { customer: Customer | undefined; source: CustomerSource | undefined }
    | undefined
  >(
    isReady ? `/opportunities/${projectId}/customer` : null,
    async () => {
      let customer: Customer | undefined;
      let source: CustomerSource | undefined;

      if (shouldRunCustomerProfileSearch) {
        try {
          resetCustomerProfile();

          if (opportunity?.partyId) {
            const CpResponse = await customerProfile?.searchCustomer?.(
              {
                partyUID: opportunity.partyId,
                customerType:
                  opportunity?.customerType === "Business"
                    ? "Business"
                    : "PRIVATE",
              },
              market
            );

            if (CpResponse?.customerSelection) {
              customer = mapCustomerDataToCustomer(
                market,
                language,
                CpResponse
              );
              source = "CUSTOMER_PROFILE";
            } else {
              logError(
                "Customer Profile failed. Fallback to search customer with opportunity API"
              );

              const customerResponse =
                await fetchCustomerFromOpportunity(projectId);
              customer = customerResponse;
              source = "OPPORTUNITY";
            }
          } else {
            logError(
              "PartyId is missing when searching customer. Fallback to search customer with opportunity API"
            );

            const customerResponse =
              await fetchCustomerFromOpportunity(projectId);
            customer = customerResponse;
            source = "OPPORTUNITY";
          }
        } catch (e) {
          const customerResponse =
            await fetchCustomerFromOpportunity(projectId);

          if (customerResponse) {
            logError(e, [
              {
                tagName: "message",
                tagValue:
                  "Customer Profile failed but SLA API returned customer data",
              },
            ]);
            customer = customerResponse;
            source = "OPPORTUNITY";
          }
        }
      } else {
        const customerResponse = await fetchCustomerFromOpportunity(projectId);
        customer = customerResponse;
        source = "OPPORTUNITY";
      }
      return {
        customer,
        source,
      };
    },
    {
      revalidateOnFocus: false,
    }
  );
}
